/** Direct: one thread per person for the whole business, chat and email together, with what they did beside it. */
import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router";
import type { DirectItem, HostDirectResponse } from "../../shared/talk";
import type { Attachment, HostPinView } from "../../shared/types";
import { UNREAD_CHANGED } from "../components/phone/unread";
import { Button } from "../components/ui/Button";
import { Face } from "../components/ui/Face";
import { Icon, type IconName } from "../components/ui/Icon";
import { Menu } from "../components/ui/Menu";
import { Sheet } from "../components/ui/Sheet";
import { useToast } from "../components/ui/Toast";
import { api } from "../lib/api";
import { ReplaySheet } from "../views/rooms/ReplaySheet";
import { AskSheet } from "./AskSheet";
import { AttCard } from "./attachments";
import { useStick } from "./Chat";
import { Composer } from "./Composer";
import { Bubble, Head, MailCard } from "./parts";
import { Nav } from "./phone";
import { circlePath, everyonePath } from "./places";
import { type PinChoice, PinSheet } from "./PinSheet";
import { personPin, pinItemsOf, pinKey, pinnable, registrantsOf } from "./pins";
import { talkClock } from "./questionsView";
import { useTalkContext } from "./TalkLayout";
import { useHost } from "./useHost";
import { useRemote, useTypers } from "./useTalk";
import { agoWords, clockTime, dayLabel, firstName, lineWhen, pinDay } from "./when";

const itemAt = (i: DirectItem) => (i.kind === "call" ? i.startsAt : i.createdAt);

const JOURNEY: Record<HostDirectResponse["journey"][number]["icon"], IconName> = { live: "live", gift: "gift", target: "target", play: "play", mail: "mail" };

/** Where a recording is in someone's hands now: how far, and a way to play it from the moment they last asked at. */
function WatchingCard({ w, onPlay }: { w: NonNullable<HostDirectResponse["watching"]>; onPlay: (sec: number) => void }) {
  return (
    <div className="side-card watching">
      <small>
        <i className="grp-now" /> Watching now
      </small>
      <b>{w.title}</b>
      <span>
        {talkClock(w.atSec)} of {talkClock(w.lengthSec)}
      </span>
      <span className="seenbar">
        <i style={{ width: `${w.lengthSec ? Math.min(100, Math.round((w.atSec / w.lengthSec) * 100)) : 0}%` }} />
      </span>
      {w.lastAskedAtSec === null ? null : (
        <Button size="s" icon="play" onClick={() => onPlay(w.lastAskedAtSec ?? 0)}>
          Play from {talkClock(w.lastAskedAtSec)}
        </Button>
      )}
    </div>
  );
}

/** What they did, their rooms and circles, and the files between them: the right side, or a sheet. */
function PersonSide({ d, pin, onUnpin, onPlay }: { d: HostDirectResponse; pin: HostPinView | null; onUnpin: () => void; onPlay: (webinarId: string, title: string, sec: number) => void }) {
  const first = firstName(d.person.name);
  const watching = d.watching;
  return (
    <>
      {watching ? <WatchingCard w={watching} onPlay={(sec) => onPlay(watching.webinarId, watching.title, sec)} /> : null}
      <h3>What {first} did</h3>
      <ul className="jr">
        {d.journey.map((j, i) => (
          <li key={i}>
            <Icon name={JOURNEY[j.icon]} /> {j.text}
          </li>
        ))}
      </ul>
      {d.places.length ? (
        <>
          <h3>{first}'s rooms and circles</h3>
          <div className="side-chips">
            {d.places.map((p) => (
              <Link key={`${p.webinarId}-${p.circleId}`} className="schip" to={p.circleId ? circlePath(p.circleId) : everyonePath(p.webinarId)}>
                {p.label}
              </Link>
            ))}
          </div>
        </>
      ) : null}
      {pin ? (
        <>
          <h3>Pinned on {first}'s page</h3>
          <div className="side-card">
            <b>{pin.title ?? "A pinned line"}</b>
            {pin.until_at ? <span>Until {pinDay(pin.until_at)}</span> : null}
            <Button size="s" icon="pin" onClick={onUnpin}>
              Unpin
            </Button>
          </div>
        </>
      ) : null}
      {d.files.length ? (
        <>
          <h3>Shared between you</h3>
          {d.files.map((f) => (
            <a key={f.key} className="sc-file" href={f.url} target="_blank" rel="noreferrer noopener">
              <Icon name="file" /> {f.name}
            </a>
          ))}
        </>
      ) : null}
    </>
  );
}

const subjectFor = (items: DirectItem[]) => {
  const last = items.filter((i): i is Extract<DirectItem, { kind: "email" }> => i.kind === "email").at(-1);
  if (!last?.subject) return "";
  return /^re:/i.test(last.subject) ? last.subject : `Re: ${last.subject}`;
};

export function DirectScreen() {
  const { contactId = "" } = useParams();
  const ctx = useTalkContext();
  const phone = ctx.lay === "phone";
  const upright = ctx.lay === "upright";
  const toast = useToast();
  const me = useHost();
  const typers = useTypers();
  const { data, phase, reload, setData, ping } = useRemote<HostDirectResponse>(`talk:direct:${contactId}`, () => api.talk.direct(contactId), `/talk/direct/${encodeURIComponent(contactId)}/ws`, (e) => typers.hear(e));
  const [mail, setMail] = useState(false);
  const [info, setInfo] = useState(false);
  const [asking, setAsking] = useState(false);
  const [pinning, setPinning] = useState<{ start: string | null } | null>(null);
  const [acting, setActing] = useState<string | null>(null);
  const [playing, setPlaying] = useState<{ webinarId: string; title: string; startSec: number } | null>(null);
  const hold = useRef<number | undefined>(undefined);
  const rids = data ? registrantsOf(data.items) : [];
  const ridKey = rids.join(",");
  const pins = useRemote<HostPinView[]>(rids.length ? `talk:pins:${ridKey}` : null, () => Promise.all(rids.map((r) => api.pins.forPerson(r))).then((all) => all.flatMap((r) => r.pins)));
  const pin = personPin(pins.data ?? [], rids);
  const [notice, setNotice] = useState<string | null>(null);
  const scroll = useRef<HTMLDivElement>(null);
  const onScroll = useStick(scroll, data?.items.length ?? 0, contactId);
  const read = useRef(0);
  useEffect(() => {
    setMail(false);
    read.current = 0;
  }, [contactId]);
  useEffect(() => {
    if (!data) return;
    const newest = Math.max(0, ...data.items.map(itemAt));
    if (newest > read.current) {
      read.current = newest;
      api.talk
        .readDirect(contactId, newest)
        .then(() => (window.dispatchEvent(new Event(UNREAD_CHANGED)), ctx.reloadRail()))
        .catch(() => {});
    }
  }, [data, contactId, ctx.reloadRail]);

  if (!data) return phase === "error" ? <main className="main"><p className="empty-note">We couldn't open this. Pick it again.</p></main> : <main className="main" />;
  const p = data.person;
  const first = firstName(p.name);
  const host = { name: me.name, photo: null };

  const send = (channel: "chat" | "email") => async (out: { text: string; subject: string; attachment: Attachment | null }) => {
    setNotice(null);
    try {
      await api.talk.sendDirect(contactId, { channel, body: out.text, subject: channel === "email" ? out.subject : undefined, attachments: out.attachment ? [out.attachment] : undefined, clientId: crypto.randomUUID() });
      if (channel === "email") {
        setMail(false);
        toast({ text: `Email sent to ${first}`, icon: "send" });
      }
      await reload();
    } catch (err) {
      setNotice(err instanceof Error && err.message ? err.message : "We couldn't send that. Try again.");
      throw err;
    }
  };
  const draft = () => api.talk.draftDirect(contactId).then((r) => r.draft);
  const ask = async (q: { text: string; options: string[] }) => {
    await api.talk.sendDirect(contactId, { channel: "chat", body: q.text, question: { options: q.options }, clientId: crypto.randomUUID() });
    setAsking(false);
    toast({ text: "Sent", icon: "send" });
    await reload();
  };
  const heart = (id: string, was: boolean) => {
    const set = (on: boolean) => setData((d) => (d ? { ...d, items: d.items.map((x) => (x.kind === "chat" && x.id === id ? { ...x, hearted: on } : x)) } : d));
    set(!was);
    api.talk.heartDirect(contactId, id, !was).catch(() => {
      set(was);
      toast({ text: "We couldn't save that. Try again.", icon: "info" });
    });
  };
  // Why the old pin is replaced: a person's page holds one pin, so a new one takes its place.
  const doPin = async ({ item, until }: PinChoice) => {
    const mine = (pins.data ?? []).filter((p) => p.registrant_id !== null && rids.includes(p.registrant_id));
    try {
      for (const p of mine.filter((x) => pinKey(x) === item.key)) await api.pins.remove(p.id);
      const made = await api.pins.add({ webinar_id: item.webinarId, registrant_id: item.registrantId, target_kind: item.kind, target_id: item.id, title: item.label.slice(0, 120), until_at: until });
      for (const p of mine.filter((x) => pinKey(x) !== item.key && x.id !== made.id)) await api.pins.remove(p.id);
      pins.setData([made]);
      setPinning(null);
      toast({ text: `Pinned${until ? ` until ${pinDay(until)}` : ""}`, icon: "check" });
    } catch {
      toast({ text: "We couldn't pin that. Try again.", icon: "info" });
      void pins.reload();
    }
  };
  const unpin = () => {
    if (!pin) return;
    pins.setData((pins.data ?? []).filter((p) => p.id !== pin.id));
    api.pins.remove(pin.id).then(
      () => toast({ text: "Unpinned", icon: "check" }),
      () => {
        toast({ text: "We couldn't unpin that. Try again.", icon: "info" });
        void pins.reload();
      },
    );
  };
  const play = (webinarId: string, title: string, startSec: number) => setPlaying({ webinarId, title, startSec });
  const canPin = rids.length > 0;
  const stop = () => window.clearTimeout(hold.current);
  const holdOn = (id: string) => ({
    onPointerDown: () => {
      stop();
      hold.current = window.setTimeout(() => setActing(id), 500);
    },
    onPointerUp: stop,
    onPointerLeave: stop,
    onPointerCancel: stop,
    onContextMenu: (e: React.MouseEvent) => {
      e.preventDefault();
      setActing(id);
    },
  });

  let day = "";
  const rows = data.items.map((it) => {
    const label = dayLabel(itemAt(it));
    const sep = label !== day ? <p key={`d${it.id}`} className="tl-sep">{label}</p> : null;
    day = label;
    let body: React.ReactNode = null;
    if (it.kind === "email") {
      body = (
        <MailCard key={it.id} subject={it.subject} when={clockTime(it.createdAt)} files={it.attachments}>
          {it.body}
        </MailCard>
      );
    } else if (it.kind === "update") {
      body = (
        <MailCard
          key={it.id}
          label="Update"
          subject={it.title}
          when={clockTime(it.createdAt)}
          files={it.attachments}
          replies={it.replies.map((r) => (
            <small key={r.id} className="mailc-r">
              {r.who.name}: {r.body}
            </small>
          ))}
        >
          {it.body}
        </MailCard>
      );
    } else if (it.kind === "call") {
      body = (
        <div key={it.id} className="callcard">
          <span className="cc-pic">
            <Icon name="live" />
          </span>
          <span className="cc-t">
            <small>A live call</small>
            <b>{it.title}</b>
            <span>
              {lineWhen(it.startsAt)} · {it.minutes} min
            </span>
          </span>
        </div>
      );
    } else {
      const mine = it.who.kind === "host";
      const toolbar = phone ? undefined : (
        <>
          {mine ? null : (
            <button type="button" aria-label={it.hearted ? "Take the heart back" : "Heart"} aria-pressed={it.hearted} onClick={() => heart(it.id, it.hearted)}>
              <Icon name="heart" fill={it.hearted} />
            </button>
          )}
          {canPin && pinnable(it) ? <Menu label="More" icon="more" items={[{ label: "Pin to their page", icon: "pin", onSelect: () => setPinning({ start: `message:${it.id}` }) }]} /> : null}
        </>
      );
      body = (
        <Bubble
          key={it.id}
          who={it.who}
          mine={mine}
          hearted={!mine && it.hearted}
          actions={toolbar}
          hold={phone && (!mine || (canPin && pinnable(it))) ? holdOn(it.id) : undefined}
          when={`${clockTime(it.createdAt)} · ${mine ? (it.seen ? "seen" : "sent") : "on their page"}`}
          note={
            it.moment || it.replyToPost ? (
              <>
                {it.moment ? (
                  <button type="button" className="mchip link" aria-label={`Play ${it.moment.title} from ${talkClock(it.moment.atSec)}`} onClick={() => play(it.webinarId, it.moment?.title ?? "", it.moment?.atSec ?? 0)}>
                    <Icon name="playf" /> At {talkClock(it.moment.atSec)} · {it.moment.title}
                  </button>
                ) : null}
                {it.replyToPost ? <>Replying to: {it.replyToPost.body}</> : null}
              </>
            ) : undefined
          }
        >
          {it.replyAnswer ? (
            <div className="bb-quote">
              <small>
                {first}'s answer at {talkClock(it.replyAnswer.atSec)} · {it.replyAnswer.question}
              </small>
              <span>{it.replyAnswer.text}</span>
            </div>
          ) : null}
          {it.question ? (
            <div className="poll">
              <p>{it.body}</p>
              {it.question.options.map((o, n) => (
                <span key={o} className={it.question?.answer === n ? "poll-o on" : "poll-o"}>
                  {it.question?.answer === n ? <i style={{ width: "100%" }} /> : null}
                  <b>{o}</b>
                  {it.question?.answer === n ? <Icon name="check" /> : null}
                </span>
              ))}
              <small>{it.question.answer === null ? `Waiting for ${first} to tap one` : `${first} answered: ${it.question.options[it.question.answer]}`}</small>
            </div>
          ) : (
            it.body
          )}
          {it.attachments.map((a, i) => (
            <AttCard key={`${a.name}-${i}`} a={a} host={host} />
          ))}
        </Bubble>
      );
    }
    return sep ? [sep, body] : [body];
  });

  const switcher = (
    <>
      <span className="dseg" role="tablist" aria-label="Send as">
        <button type="button" role="tab" aria-selected={!mail} className={mail ? undefined : "on"} onClick={() => setMail(false)}>
          <Icon name="chat" /> Chat
        </button>
        <button type="button" role="tab" aria-selected={mail} className={mail ? "on" : undefined} disabled={!data.fromEmail} title={data.fromEmail ? undefined : "Add a sending address in Settings first"} onClick={() => setMail(true)}>
          <Icon name="mail" /> Email
        </button>
      </span>
      {data.lastOnPageAt ? (
        <small className="dc-seen">
          <i /> On their page {phone ? agoWords(data.lastOnPageAt).replace("hours", "h").replace("hour", "h") : agoWords(data.lastOnPageAt)}
        </small>
      ) : null}
    </>
  );
  const composer = (
    <Composer
      key={mail ? "mail" : "chat"}
      to={first}
      phone={phone}
      host={host}
      send={mail ? "Send email" : "Send"}
      placeholder={mail ? `Write your email to ${first}...` : phone ? `Write to ${first}` : `Write to ${first}...`}
      draft={mail ? undefined : draft}
      onSend={(out) => send(mail ? "email" : "chat")(out)}
      onAsk={mail ? undefined : () => setAsking(true)}
      askInPlus
      away={asking}
      onTyping={mail ? undefined : ping}
      notice={notice}
      head={mail && phone ? undefined : switcher}
      mail={mail ? { from: data.fromName, fromEmail: data.fromEmail, to: { name: p.name, photo: p.photoUrl }, subject: subjectFor(data.items), title: `Email to ${first}`, onCancel: () => setMail(false) } : undefined}
    />
  );
  const typing = typers.who(() => first).length ? (
    <p className="typing">
      <span>
        <i />
        <i />
        <i />
      </span>
      {first} is typing
    </p>
  ) : null;
  const actingLine = data.items.find((x): x is Extract<DirectItem, { kind: "chat" }> => x.kind === "chat" && x.id === acting) ?? null;
  const sheet = (
    <>
      <Sheet open={info} onClose={() => setInfo(false)} title={`About ${first}`}>
        <div className="sheet-b side-sheet">
          <PersonSide d={data} pin={pin} onUnpin={unpin} onPlay={play} />
        </div>
      </Sheet>
      {playing ? <ReplaySheet webinarId={playing.webinarId} title={playing.title} startSec={playing.startSec} onClose={() => setPlaying(null)} /> : null}
      <AskSheet open={asking} to={first} onClose={() => setAsking(false)} onSend={ask} />
      {pinning ? (
        <PinSheet
          open
          first={first}
          hostFirst={firstName(me.name)}
          items={pinItemsOf(data.items)}
          start={pinning.start}
          current={pin ? { key: pinKey(pin), until: pin.until_at } : null}
          onClose={() => setPinning(null)}
          onPin={doPin}
        />
      ) : null}
      {actingLine ? (
        <div className="hp-veil" onClick={() => setActing(null)}>
          <div className="hp-acts" role="menu" aria-label={`${actingLine.who.name}'s line`} onClick={(e) => e.stopPropagation()}>
            <div className="hp-acts-card">
              <p className="hp-acts-h">
                <Face name={actingLine.who.name} photo={actingLine.who.photoUrl} size={32} />
                <span>
                  <b>{actingLine.who.name}</b>
                  <small>{actingLine.body}</small>
                </span>
              </p>
              {actingLine.who.kind === "member" ? (
                <button type="button" role="menuitem" onClick={() => (setActing(null), heart(actingLine.id, actingLine.hearted))}>
                  <Icon name="heart" />
                  {actingLine.hearted ? "Take the heart back" : "Heart"}
                </button>
              ) : null}
              {canPin && pinnable(actingLine) ? (
                <button type="button" role="menuitem" onClick={() => (setActing(null), setPinning({ start: `message:${actingLine.id}` }))}>
                  <Icon name="pin" />
                  Pin to their page
                </button>
              ) : null}
            </div>
            <button type="button" className="hp-acts-x" onClick={() => setActing(null)}>
              Cancel
            </button>
          </div>
        </div>
      ) : null}
    </>
  );

  if (phone) {
    return (
      <div className="hp">
        <Nav
          back="Direct"
          to="/conversations?tab=direct"
          title={p.name}
          sub={data.watching ? `Watching now · ${talkClock(data.watching.atSec)}` : (p.tag ?? undefined)}
          right={
            <button type="button" className="hp-ib" aria-label={`About ${first}`} onClick={() => setInfo(true)}>
              <Icon name="info" />
            </button>
          }
        />
        <div className="hp-chat" ref={scroll} onScroll={onScroll}>
          {rows}
          {typing}
        </div>
        {composer}
        {sheet}
      </div>
    );
  }
  return (
    <>
      <main className="main">
        <Head
          pic={<Face name={p.name} photo={p.photoUrl} size={42} />}
          title={p.name}
          sub={data.watching ? `${p.tag ? `${p.tag} · ` : ""}watching ${data.watching.title} now` : (p.tag ?? "")}
          right={
            upright ? (
              <button type="button" className="ib" aria-label={`About ${first}`} onClick={() => setInfo(true)}>
                <Icon name="info" />
              </button>
            ) : undefined
          }
        />
        <div className="thread" ref={scroll} onScroll={onScroll}>
          {rows}
          {typing}
        </div>
        {composer}
      </main>
      <aside className="side">
        <PersonSide d={data} pin={pin} onUnpin={unpin} onPlay={play} />
      </aside>
      {sheet}
    </>
  );
}
